import { t } from "@lingui/macro";
import { captureEvent } from "@sentry/react";
import {
  CreateTaskGroupTemplateDocument,
  type CreateTaskGroupTemplateMutation,
  type CreateTaskGroupTemplateMutationVariables,
  type TaskGroupTemplateFragment,
} from "@src/__generated__/graphql";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import type { AppStore } from "@src/stores/AppStore";
import { required } from "@src/utils/forms/validators";
import { DisclosureState } from "@src/utils/mobx/states/DisclosureState";
import { FieldState } from "formstate";
import { action, makeObservable } from "mobx";

type TaskGroupTemplateCreateModalDisclosureState = {
  taskGroupTemplate?: TaskGroupTemplateFragment;
  onSubmit: (taskGroupTemplate: TaskGroupTemplateFragment) => void;
};
export class TaskGroupTemplateCreateModalStore {
  name = new FieldState<string | undefined>(undefined);
  disclosure = new DisclosureState<TaskGroupTemplateCreateModalDisclosureState>(
    {
      onOpen: (additionalData) => {
        this.setNameFieldState(additionalData?.taskGroupTemplate?.name);
      },
      onClose: () => {
        this.setNameFieldState();
      },
    },
  );

  mutator = new MutationHelper<
    CreateTaskGroupTemplateMutation,
    CreateTaskGroupTemplateMutationVariables
  >(CreateTaskGroupTemplateDocument);

  constructor(private appStore: AppStore) {
    makeObservable(this);
  }

  @action.bound setNameFieldState(initValue?: string) {
    this.name = new FieldState(initValue).validators(required);
  }

  @action.bound async createTemplateGroup(name: string) {
    const [data, error] = await this.mutator.mutate({
      name,
    });

    if (error) {
      this.appStore.UIStore.toast({
        status: "error",
      });
      return;
    }

    this.disclosure.additionalData?.onSubmit(data.createTaskTemplateGroup);
    this.appStore.UIStore.toast({
      status: "success",
      title: t`Task group created successfully.`,
    });
  }

  @action.bound handleCancel() {
    this.disclosure.onClose();
  }

  @action.bound async handleConfirm() {
    const { hasError } = await this.name.validate();

    if (hasError) return;

    if (!this.name.$) {
      captureEvent({
        message: "FE: TaskGroupTemplateModal name isn't defined.",
      });
      this.appStore.UIStore.toast({
        status: "error",
      });
      return;
    }

    await this.createTemplateGroup(this.name.$);
    this.handleCancel();
  }
}
